import * as z from 'zod/v4';

import { dateTimeSchema } from '../frameworks/entry.js';
import { currencySchema } from '../lib/entry.js';
import { couponProductSchema } from '../product/entry.js';
import {
  type BettingEvent,
  type BettingEventId,
  type Country,
  type CountryId,
  eventObjectTransformSchema,
  type EventStatisticsKey,
  type EventTypeStatistic,
  type FallbackEventId,
  getFallbackEventId,
  type League,
  type LeagueId,
  minimalDrawEvent,
  type Participant,
  type ParticipantId,
  sportSchema,
} from '../sport/entry.js';
import { getDrawId } from './DrawId.js';
import { drawNumberSchema } from './DrawNumber.js';
import { disableTypeEnum, drawStateSchema } from './DrawState.js';
import { type Fund, fundsTransformSchema } from './Fund.js';

export type RelatedDraw = z.infer<typeof relatedDrawTransformSchema>;

const relatedDrawTransformSchema = z
  .object({
    drawNumber: drawNumberSchema,
    regCloseTime: dateTimeSchema,
    productId: couponProductSchema,
  })
  .transform(({ productId, regCloseTime, drawNumber }) => {
    return {
      drawNumber,
      regCloseTime,
      product: productId,
    };
  });

const drawBaseSchema = z.object({
  drawNumber: drawNumberSchema,
  cancelCloseTime: dateTimeSchema,
  regCloseTime: dateTimeSchema,
  regOpenTime: dateTimeSchema,
  retailerCancelCloseTime: dateTimeSchema,
  retailerCloseTime: dateTimeSchema,
  retailerOpenTime: dateTimeSchema,
  lastDateWithoutTimeOfDay: dateTimeSchema.optional().nullish(),
  regCloseDescription: z.string(),
  rowPrice: currencySchema,
  seasonLastDraw: z.boolean(),
  bombenDrawNum: drawNumberSchema.optional(),
  currentNetSale: currencySchema,
  betIndex: z.number(),
  drawComment: z.string(),

  regBetDisabled: disableTypeEnum,
  relatedDraws: z.array(relatedDrawTransformSchema),

  productId: couponProductSchema,
  drawStateId: drawStateSchema,
  sport: sportSchema,
  fund: fundsTransformSchema,
});

/**
 * Zod schema for 1X2 draw API response type
 * Used to parse the response from the API into a Draw entity
 */
const oneXTwoDrawSchema = z.object({
  ...drawBaseSchema.shape,
  drawEvents: z.array(z.union([eventObjectTransformSchema, minimalDrawEvent])),
});

type CompleteEventObject = z.infer<typeof eventObjectTransformSchema>['eventObject'] & { status: 'COMPLETE' };
type MinimalEventObject = z.infer<typeof minimalDrawEvent>['eventObject'] & {
  status: 'MINIMAL';
  fallbackEventId: FallbackEventId;
};

/**
 * Zod schema for result draw API response type
 * Used to parse the response from the API into a Draw entity
 */
const resultDrawSchema = z
  .object({
    ...drawBaseSchema.shape,
    events: z.array(eventObjectTransformSchema),
  })
  .transform(({ events, ...drawObject }) => ({
    ...drawObject,
    drawEvents: events,
  }));

/**
 * Composed Zod schema for different variations of draw API payloads
 * Used to parse the response from the API into a Draw entity
 */
export const drawTransformSchema = z
  .union([oneXTwoDrawSchema, resultDrawSchema])
  .transform(({ productId, sport, drawEvents, fund, ...drawObject }) => {
    // This object will result in the Draw typescript type
    const draw = {
      drawNumber: drawObject.drawNumber,
      cancelCloseTime: drawObject.cancelCloseTime,
      regCloseTime: drawObject.regCloseTime,
      regOpenTime: drawObject.regOpenTime,
      retailerCancelCloseTime: drawObject.retailerCancelCloseTime,
      retailerCloseTime: drawObject.retailerCloseTime,
      retailerOpenTime: drawObject.retailerOpenTime,
      lastDateWithoutTimeOfDay: drawObject.lastDateWithoutTimeOfDay,
      regCloseDescription: drawObject.regCloseDescription,
      rowPrice: drawObject.rowPrice,
      seasonLastDraw: drawObject.seasonLastDraw,
      bombenDrawNum: drawObject.bombenDrawNum,
      currentNetSale: drawObject.currentNetSale,
      betIndex: drawObject.betIndex,
      drawComment: drawObject.drawComment,
      regBetDisabled: drawObject.regBetDisabled,
      relatedDraws: drawObject.relatedDraws,
      drawState: drawObject.drawStateId,
      product: productId,
      sportType: sport.type,
      events: drawEvents.map((drawEvent): MinimalEventObject | CompleteEventObject => {
        if (drawEvent.status === 'MINIMAL') {
          const fallbackEventId = getFallbackEventId(
            productId,
            drawObject.drawNumber,
            drawEvent.eventObject.eventNumber
          );
          return { ...drawEvent.eventObject, status: drawEvent.status, fallbackEventId };
        }
        return { ...drawEvent.eventObject, status: drawEvent.status };
      }),
      drawId: getDrawId(productId, drawObject.drawNumber),
    };

    const { eventTypeStatistics, participants, countries, leagues, bettingEvent } = drawEvents.reduce(
      (acc, drawEvent) => {
        if (drawEvent.status === 'MINIMAL') {
          return acc;
        }

        const { eventTypeStatistics, participants, countries, league, bettingEvent, eventObject } = drawEvent;

        if (eventTypeStatistics) {
          acc.eventTypeStatistics[eventTypeStatistics.id] = eventTypeStatistics;
        }

        acc.bettingEvent[eventObject.bettingEventId] = bettingEvent;

        Object.assign(acc.participants, participants);
        Object.assign(acc.countries, countries);
        acc.leagues[league.id] = league;

        return acc;
      },
      {
        participants: {} as Record<ParticipantId, Participant>,
        countries: {} as Record<CountryId, Country>,
        leagues: {} as Record<LeagueId, League>,
        eventTypeStatistics: {} as Record<EventStatisticsKey, EventTypeStatistic>,
        bettingEvent: {} as Record<BettingEventId, BettingEvent>,
      }
    );

    const normalizedFund: Fund = {
      ...fund,
      drawNumber: draw.drawNumber,
      product: draw.product,
    };

    return {
      bettingEvent,
      participants,
      countries,
      leagues,
      eventTypeStatistics,
      draw,
      sport,
      fund: normalizedFund,
    };
  });

export type DrawData = z.infer<typeof drawTransformSchema>;
export type Draw = DrawData['draw'];
